/**
 * Unify Device model.
 *
 * Represents physical equipment at a practice site or room, or telehealth
 * integrations. For rooms & equipment see
 * docs/prd/LocationsRoomsEquipment/PRD_LOCATIONS_ROOMS_EQUIPMENT_FHIR_MAPPING.md.
 */
import type { Device, Extension, Location, Organization, Reference } from '@medplum/fhirtypes';

import { DEVICE_IDENTIFIER_SYSTEM, DEVICE_PORTABILITY_EXT } from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  Literal union types
// ═══════════════════════════════════════════════════════════════════════

/** Device statuses used by Unify. */
export type DeviceStatus = 'active' | 'inactive' | 'entered-in-error';

/** Device name types used by Unify. */
export type DeviceNameType = 'user-friendly-name' | 'model-name' | 'other';

export type DevicePortabilityCode = 'fixed' | 'portable';

// ═══════════════════════════════════════════════════════════════════════
//  Private helpers
// ═══════════════════════════════════════════════════════════════════════

function getExt(resource: Device, url: string): Extension | undefined {
  return resource.extension?.find((e) => e.url === url);
}

function setExt(resource: Device, url: string, ext: Partial<Extension>): Device {
  const extensions = (resource.extension ?? []).filter((e) => e.url !== url);
  extensions.push({ url, ...ext } as Extension);
  return { ...resource, extension: extensions };
}

export function getDevicePortability(resource: Device): DevicePortabilityCode | undefined {
  const code = getExt(resource, DEVICE_PORTABILITY_EXT)?.valueCode;
  if (code === 'fixed' || code === 'portable') return code;
  return undefined;
}

export function setDevicePortability(resource: Device, value: DevicePortabilityCode): Device {
  return setExt(resource, DEVICE_PORTABILITY_EXT, { valueCode: value });
}

export interface PracticeEquipmentDeviceInput {
  name: string;
  portability: DevicePortabilityCode;
  /** Room Location id when fixed; site Location id when portable. */
  locationId: string;
  locationDisplay?: string;
  status: DeviceStatus;
}

/**
 * Build a FHIR Device for practice equipment (fixed or portable).
 */
export function buildPracticeEquipmentDevice(
  organizationId: string,
  organizationDisplay: string | undefined,
  input: PracticeEquipmentDeviceInput,
  existing?: Device,
): Device {
  const orgRef: Reference<Organization> = {
    reference: `Organization/${organizationId}`,
    display: organizationDisplay,
  };
  const locRef: Reference<Location> = {
    reference: `Location/${input.locationId}`,
    display: input.locationDisplay,
  };
  const identifier =
    existing?.id != null
      ? [
          ...(existing.identifier?.filter((i) => i.system !== DEVICE_IDENTIFIER_SYSTEM) ?? []),
          { system: DEVICE_IDENTIFIER_SYSTEM, value: existing.id },
        ]
      : existing?.identifier;

  let dev: Device = {
    resourceType: 'Device',
    ...(existing?.id ? { id: existing.id } : {}),
    ...(identifier && identifier.length > 0 ? { identifier } : {}),
    status: input.status === 'active' ? 'active' : 'inactive',
    deviceName: [{ name: input.name, type: 'user-friendly-name' }],
    location: locRef,
    owner: orgRef,
    extension: [],
  };
  dev = setDevicePortability(dev, input.portability);
  return dev;
}

// ═══════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════

/** Returns the device's user-friendly display name. */
export function getDeviceDisplayName(device: Device): string | undefined {
  return device.deviceName?.find((n) => n.type === 'user-friendly-name')?.name
    ?? device.deviceName?.[0]?.name;
}

/** Returns the owning Organization reference. */
export function getDeviceOwnerRef(device: Device): Reference | undefined {
  return device.owner;
}

/** Returns the Location reference. */
export function getDeviceLocationRef(device: Device): Reference | undefined {
  return device.location;
}

/** Returns whether the device is active. */
export function isDeviceActive(device: Device): boolean {
  return device.status === 'active';
}

/** Returns an identifier value for a given system. */
export function getDeviceIdentifierValue(device: Device, system: string): string | undefined {
  return device.identifier?.find((i) => i.system === system)?.value;
}
